import { App } from 'antd';
import { useCallback, useState } from 'react';
import { DormitoryHttp } from '@/services';
import { parseError } from '@/utils';
import { useTranslation } from 'react-i18next';

export default function useDormitoryGroupManagement() {

  const [t] = useTranslation(['common', 'dormitory']);
  const [group, setGroup] = useState<DormitoryGroup>(null);
  const [groupBlocksError, setGroupBlocksError] = useState('');
  const [groupBlocks, setGroupBlocks] = useState<DormitoryGroupRoomsBlock[]>([]);
  const [fetching, setFetching] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [fetchingGroupBlocks, setFetchingGroupBlocks] = useState(false);
  const { message } = App.useApp();

  const fetchGroup = useCallback(async () => {
    setFetching(true);
    setGroup(null);
    try {
      const res = await DormitoryHttp.fetchDormitoryGroup();
      setGroup(res.data.payload);
    }
    catch (e) {
      console.error(e);
    }
    finally {
      setFetching(false);
    }
  }, []);

  const invite = useCallback(async (username: string, callback?: () => void) => {
    setInviting(true);
    try {
      await DormitoryHttp.inviteMember(username);
      message.success(t('dormitory:messages:success-invite-send'));
      if (typeof callback === 'function') callback();
    }
    catch (e) {
      console.error(e);
    }
    finally {
      setInviting(false);
    }
  }, [message, t]);

  const acceptInvite = useCallback(async (callback?: () => void) => {
    setAccepting(true);
    try {
      await DormitoryHttp.acceptinvite();
      message.success(t('dormitory:messages.success-join'));
      if (typeof callback === 'function') callback();
    }
    catch (e) {
      console.error(e);
    }
    finally {
      setAccepting(false);
    }
  }, [message, t]);

  const fetchGroupBlocksRooms = useCallback(async () => {
    setFetchingGroupBlocks(true);
    try {
      setGroupBlocks([]);
      setGroupBlocksError(null);
      const res = await DormitoryHttp.fetchGroupRooms();
      setGroupBlocks(res.data?.payload?.dormitoryBlocks ?? []);
    }
    catch (e) {
      console.error(e);
      setGroupBlocksError(parseError(e));
    }
    finally {
      setFetchingGroupBlocks(false);
    }
  }, []);

  const rejectInvite = useCallback(async (callback?: () => void) => {
    setRejecting(true);
    try {
      await DormitoryHttp.rejectInvite();
      message.success(t('dormitory:messages.success-reject-group-invite'));
      if (typeof callback === 'function') callback();
    }
    catch (e) {
      console.error(e);
    }
    finally {
      setRejecting(false);
    }
  }, [message, t]);

  const removeUserFromGroup = useCallback(async (studentNumber: string, solitary: boolean = false) => {
    try {
      await DormitoryHttp.removeUserFromGroup({ solitary, studentNumber });
    }
    catch (e) {
      console.log(e);
    }
  }, []);

  const removeGroup = useCallback(async () => {
    try {
      await DormitoryHttp.removeDormitoryGroup();
    }
    catch (e) {
      console.log(e);
    }
  }, []);

  const removeRoom = useCallback(async (dormitoryRoomId: number, solitary: boolean) => {
    try {
      await DormitoryHttp.updateDormitoryGroup({
        solitary,
        removeDormitoryRoom: true,
        dormitoryRoomId: dormitoryRoomId
      });
      message.success({ content: t('dormitory:messages.success-remove') });
    }
    catch (e) {
      console.log(e);
    }
  }, [message, t]);

  return {
    group,
    fetchGroup,
    fetching,
    acceptInvite,
    accepting,
    rejecting,
    removeRoom,
    removeGroup,
    rejectInvite,
    invite,
    inviting,
    groupBlocks,
    groupBlocksError,
    removeUserFromGroup,
    fetchGroupBlocksRooms,
    fetchingGroupBlocks
  };
}
